/**
 * 遊戲載入「軟逾時」計時（配 `SlowNetworkDialog.vue`）。
 *
 * 🛑 **只有軟逾時、沒有硬逾時**：時間到只跳對話框（刷新 / 等待），**永不中斷載入**。
 *   理由：遊戲包在慢速網路本來就可能載好幾分鐘，任何絕對時長都會誤殺「再等 10 秒就能玩」的玩家。
 *
 * 量的是「**停滯多久**」不是「總共多久」：每過一個里程碑就 `arm()` 重新計時，
 * 所以慢速但持續推進的載入不會跳框，真的卡住才會。里程碑：
 *
 *   iframe 開始載 ──arm──> onGameLoad ──arm──> GAME_READY ──┬─ 舊款：disarm（遊戲自己接手）
 *                                                          └─ 新款（hostLoading）：arm('firstAlive')
 *                                                               ──每則 LOAD_ALIVE──> arm('loadAlive')
 *                                                               ──LOAD_COMPLETE──> disarm
 *
 * 新款最後一段有**心跳**（Cocos 每 5s 一則 `LOAD_ALIVE`，與資源載入解耦；boot 死掉時心跳也停）：
 *   - 還沒收到第一則心跳前用 `firstAlive`（15s）
 *   - 收到第一則之後改用 `loadAlive`（15s = 漏 3 拍才跳框），之後每則 re-arm
 *   這樣「boot 在 GAME_READY 之後死掉」（Cocos 端已知的 401 殘洞）15s 內就會給玩家逃生門，
 *   而不是永遠轉圈。見 `docs/LOADING_PROTOCOL.md`〈六〉。
 *
 * 玩家選「等待」→ `dismiss()` 收起對話框並**重新計時**（再停滯一次會再跳）。
 * 玩家選「刷新」→ 由 caller reload iframe（**不要 reload 整頁**：ticket 已消耗，整頁重載會登入失敗），
 *   然後呼叫 `arm()` 重新開始。
 */
import { onUnmounted, ref } from 'vue'

/** 各階段的停滯門檻（毫秒）。 */
export const SLOW_MS = {
  /** iframe 開始載 → onGameLoad；直接受行動網路下載速度影響，留 45s 容忍。 */
  iframeLoad: 45_000,
  /** onGameLoad → GAME_READY；同時受後續 script 載入與 Cocos 初始化效能影響。 */
  gameReady: 30_000,
  /**
   * GAME_READY → **第一則 LOAD_ALIVE**。
   *
   * 2026-08-20 Cocos 回覆確認：首則心跳固定在 `GAME_READY` 後 **5.0 秒**，且心跳是
   * bridge 共用層能力 —— 會送 `hostLoading: true` 的一定是更新過 bridge 的版本，
   * **「送了 hostLoading 卻沒有心跳」不存在**。所以這段從原本保守的 45s 收到 15s，
   * 與 `loadAlive` 同一條規則（漏 3 拍）。
   *
   * 🛑 別再放寬回 45s：Cocos 若在 GAME_READY 之後就死掉（他們已知的 401 殘洞，
   *   見 `docs/LOADING_PROTOCOL.md`〈六〉），45s 表示玩家要乾等 45 秒才有逃生門。
   *   實測 `GAME_READY → LOAD_COMPLETE` 約 9~13s（有快取 2.4s），但那段有心跳一路 re-arm，
   *   不會因為載得久而誤跳框。
   */
  firstAlive: 15_000,
  /** 兩則 LOAD_ALIVE 之間（Cocos 每 5s 一則 → 15s = 漏 3 拍）。收到第一則心跳後才用這段 */
  loadAlive: 15_000,
} as const

export type LoadStage = keyof typeof SLOW_MS

export function useLoadTimeout() {
  /** 對話框是否顯示 */
  const slowVisible = ref(false)
  /** 目前在等哪一段（診斷 / log 用；null = 沒在計時） */
  const stage = ref<LoadStage | null>(null)

  let timer: ReturnType<typeof setTimeout> | undefined

  const clear = (): void => {
    if (timer !== undefined) {
      clearTimeout(timer)
      timer = undefined
    }
  }

  /** 開始（或重新開始）計某一段的停滯時間。 */
  const arm = (next: LoadStage): void => {
    clear()
    stage.value = next
    timer = setTimeout(() => {
      // 逾時只是跳框，不改變任何載入狀態
      console.warn(`[web][Loading] slow timeout at stage: ${next} (${SLOW_MS[next]}ms)`)
      slowVisible.value = true
    }, SLOW_MS[next])
  }

  /** 載入完成 / 不再需要計時（也會收起對話框）。 */
  const disarm = (): void => {
    clear()
    stage.value = null
    slowVisible.value = false
  }

  /** 玩家選「等待」：收起對話框，同一段重新計時（再卡一次會再跳）。 */
  const dismiss = (): void => {
    slowVisible.value = false
    if (stage.value) arm(stage.value)
  }

  /**
   * 立刻跳框、不等計時（`LOAD_ERROR` 用：Cocos 明說載入失敗，沒必要再等 15s）。
   * 停錶但**保留 stage**：玩家選「等待」仍會同段重新起錶（跟逾時跳的框行為一致）。
   */
  const show = (): void => {
    clear()
    slowVisible.value = true
  }

  onUnmounted(clear)

  return { slowVisible, stage, arm, disarm, dismiss, show }
}
